/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { ShopperProducts } from '@/scapi';
import type { AppConfig } from '@/types/config';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { useProductSetsBundles } from '@/hooks/product/use-product-sets-bundles';
import { useProductActions } from '@/hooks/product/use-product-actions';
import { useProductImages } from '@/hooks/product/use-product-images';
import { ProductBadge } from '@/components/product-badges/product-badges';
import { productBadgeColorClasses } from '@/components/product-badges/product-badge-variants';
import { productHasBadge } from '@/lib/product/product-badges';
import DeferredProductRecommendations from '@/components/product-recommendations/deferred';
import { EINSTEIN_RECOMMENDERS } from '@/lib/adapters/engagement/einstein-recommenders';
import ProductBundleMedia from './product-bundle-media';
import ProductBundleDetails from './product-bundle-details';
import PurchaseThemSeparately from './purchase-them-separately';
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { useStoreLocator } from '@/extensions/store-locator/providers/store-locator';
// @sfdc-extension-block-end SFDC_EXT_BOPIS

interface ProductBundleViewProps {
    product: ShopperProducts.schemas['Product'];
    /** Page Designer page promise carrying the `bundleMarketingContent` region. */
    page: Promise<PageWithComponentData | null> | PageWithComponentData | null;
}

/**
 * Product Bundle Page layout for the Gift Shop redesign (MEM-10571). Renders only for
 * `isProductBundle(product)` — product sets keep the existing `ProductView` + `ChildProducts` UI.
 *
 * Unlike sets/"build your own" bundles, this bundle's children are fixed standard products (no variant
 * selection), so `useProductSetsBundles` auto-selects them and add-to-cart is gated purely on availability —
 * there's no per-child picker grid here. The bundle's contents are instead surfaced as an informational
 * upsell (`PurchaseThemSeparately`), matching the visual spec.
 */
export default function ProductBundleView({ product, page }: ProductBundleViewProps): ReactElement {
    const { t } = useTranslation('product');

    // @sfdc-extension-line SFDC_EXT_BOPIS
    const selectedStore = useStoreLocator((state) => state.selectedStoreInfo);

    const {
        isAddingToOrUpdatingCart,
        handleProductBundleAddToCart,
        // @sfdc-extension-line SFDC_EXT_BOPIS
        basketPickupStore,
    } = useProductActions({ product });

    const {
        comboProduct,
        selectedBundleQuantity,
        setSelectedBundleQuantity,
        isCompletelyOutOfStock,
        productWithCalculatedInventory,
        effectiveQuantity,
        bundleStockLevel,
        bundleOutOfStock,
    } = useProductSetsBundles({
        product,
        // @sfdc-extension-block-start SFDC_EXT_BOPIS
        selectedStoreInventoryId: selectedStore?.inventoryId,
        basketPickupStore,
        // @sfdc-extension-block-end SFDC_EXT_BOPIS
    });

    const childProducts = comboProduct.childProducts || [];
    // This page has no per-child variant picker (unlike ChildProducts, used for sets/"build your own"
    // bundles) — children are fixed and auto-selected by useProductSetsBundles. Add-to-cart therefore
    // depends only on availability, not on selection completeness/orderability of individual children.
    const canAddToCart = !isCompletelyOutOfStock;

    const handleAddToCart = () => {
        // Submit every bundle child as-is (not `childProductSelection`, which `useProductSetsBundles` only
        // auto-populates for `isStandardProduct` children — real bundle data doesn't always mark every
        // child that way, so relying on it here could submit an empty/partial selection). This page has no
        // variant picker, so there's nothing to select: send the full fixed configuration.
        const childSelections = childProducts.map((childProduct) => ({
            product: childProduct,
            quantity: (childProduct as { quantity?: number }).quantity ?? 1,
        }));
        void handleProductBundleAddToCart(selectedBundleQuantity, childSelections);
    };

    const { galleryImages } = useProductImages({ product });

    // "NEW" badge on the main gallery image, driven by the same badge config used on PLP tiles/PDP.
    const config = useConfig<AppConfig>();
    const newBadgeConfig = config.global.badges?.find((badge) => badge.value?.toLowerCase() === 'new');
    const isNewProduct = Boolean(
        newBadgeConfig && productHasBadge(product as unknown as Record<string, unknown>, newBadgeConfig)
    );
    const newBadge =
        isNewProduct && newBadgeConfig ? (
            <ProductBadge className={productBadgeColorClasses[newBadgeConfig.color]}>
                {newBadgeConfig.label}
            </ProductBadge>
        ) : undefined;

    return (
        <div className="space-y-12">
            <ProductBundleMedia page={page} images={galleryImages} productName={product.name} badge={newBadge} />

            <ProductBundleDetails
                product={product}
                selectedBundleQuantity={selectedBundleQuantity}
                setSelectedBundleQuantity={setSelectedBundleQuantity}
                bundleStockLevel={bundleStockLevel}
                bundleOutOfStock={bundleOutOfStock}
                canAddToCart={canAddToCart}
                isAddingToOrUpdatingCart={isAddingToOrUpdatingCart}
                onAddToCart={handleAddToCart}
                // @sfdc-extension-line SFDC_EXT_BOPIS
                productWithCalculatedInventory={productWithCalculatedInventory}
                // @sfdc-extension-line SFDC_EXT_BOPIS
                effectiveQuantity={effectiveQuantity}
                // @sfdc-extension-line SFDC_EXT_BOPIS
                basketPickupStore={basketPickupStore}
            />

            <PurchaseThemSeparately childProducts={childProducts} />

            <DeferredProductRecommendations
                recommenderName={EINSTEIN_RECOMMENDERS.PDP_MIGHT_ALSO_LIKE}
                recommenderTitle={t('recommendations.topTrendingGifts')}
                className="max-w-none px-0"
            />

            <DeferredProductRecommendations
                recommenderName={EINSTEIN_RECOMMENDERS.CART_MAY_ALSO_LIKE}
                recommenderTitle={t('recommendations.customersAlsoPurchased')}
                className="max-w-none px-0"
            />
        </div>
    );
}
